import {
	Badge,
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Icon,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Spinner,
	Switch,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	useColorModeValue,
	useDisclosure
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdPersonAdd, MdAttachMoney, MdDelete, MdEdit, MdGavel, MdLocalOffer, MdVpnKey } from 'react-icons/md';
import Card from '../../components/card/Card';
import PasswordInput from '../../components/passwordInput/PasswordInput';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import {
	adjustUserCredits,
	createUser,
	editUser,
	editUserLeilao,
	editUserPrice,
	fetchUsers,
	removeUser,
	setUserPassword,
	toggleUserActive
} from '../../store/features/user/user.slice';
import { fetchIndicators } from '../../store/features/indicator/indicator.slice';
import { Role, User } from '../../models/user.model';
import { toastService } from '../../services/toast.service';
import { formatBRL, formatPrice, formatQueries } from '../../utils/format';

export default function Usuarios() {
	const dispatch = useAppDispatch();
	const { list, loading } = useAppSelector((state: RootState) => state.user);
	const indicators = useAppSelector((state: RootState) => state.indicator.list);
	const { role, userId } = useAppSelector((state: RootState) => state.auth);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');

	const isAdmin = role === Role.ADMIN;

	const createModal = useDisclosure();
	const creditsModal = useDisclosure();
	const priceModal = useDisclosure();
	const leilaoModal = useDisclosure();
	const editModal = useDisclosure();
	const passwordModal = useDisclosure();
	const deleteModal = useDisclosure();

	const [selected, setSelected] = useState<User | null>(null);
	const [form, setForm] = useState({ name: '', email: '', password: '', role: Role.USER, pricePerQuery: '', indicatorId: '' });
	const [editForm, setEditForm] = useState({ name: '', email: '', role: Role.USER, indicatorId: '' });
	const [newPassword, setNewPassword] = useState('');
	// String para permitir digitar "-" e negativos (input type=number bloqueia isso).
	const [creditAmount, setCreditAmount] = useState('');
	const [creditDesc, setCreditDesc] = useState('');
	// String para permitir digitar decimais (ex.: "0.15") sem o input comer o ponto.
	const [priceValue, setPriceValue] = useState('');
	// Preços do leilão por cliente. Vazio = sem override (cai no padrão global).
	const [leilaoToday, setLeilaoToday] = useState('');
	const [leilaoOld, setLeilaoOld] = useState('');

	useEffect(() => {
		dispatch(fetchUsers());
		dispatch(fetchIndicators());
	}, [dispatch]);

	const handleCreate = async () => {
		if (!form.name || !form.email || !form.password) {
			toastService.warning('Preencha nome, e-mail e senha');
			return;
		}
		const price = Number(form.pricePerQuery) || 0;
		if (price < 0) {
			toastService.warning('O preço por consulta não pode ser negativo');
			return;
		}
		await dispatch(
			createUser({
				name: form.name,
				email: form.email,
				password: form.password,
				role: form.role,
				pricePerQuery: price,
				indicatorId: form.indicatorId || undefined
			})
		);
		setForm({ name: '', email: '', password: '', role: Role.USER, pricePerQuery: '', indicatorId: '' });
		createModal.onClose();
	};

	const openCredits = (user: User) => {
		setSelected(user);
		setCreditAmount('');
		setCreditDesc('');
		creditsModal.onOpen();
	};

	const openPrice = (user: User) => {
		setSelected(user);
		setPriceValue(user.pricePerQuery != null ? String(user.pricePerQuery) : '');
		priceModal.onOpen();
	};

	const openLeilao = (user: User) => {
		setSelected(user);
		setLeilaoToday(user.leilaoPriceToday != null ? String(user.leilaoPriceToday) : '');
		setLeilaoOld(user.leilaoPriceOld != null ? String(user.leilaoPriceOld) : '');
		leilaoModal.onOpen();
	};

	const openEdit = (user: User) => {
		setSelected(user);
		setEditForm({ name: user.name, email: user.email, role: user.role, indicatorId: user.indicatorId || '' });
		editModal.onOpen();
	};

	const openPassword = (user: User) => {
		setSelected(user);
		setNewPassword('');
		passwordModal.onOpen();
	};

	const handleEdit = async () => {
		if (!selected) return;
		if (!editForm.name || !editForm.email) {
			toastService.warning('Preencha nome e e-mail');
			return;
		}
		await dispatch(
			editUser({
				id: selected.id,
				name: editForm.name,
				email: editForm.email,
				role: editForm.role,
				indicatorId: editForm.indicatorId || null
			})
		);
		editModal.onClose();
	};

	const openDelete = (user: User) => {
		setSelected(user);
		deleteModal.onOpen();
	};

	const handleDelete = async () => {
		if (!selected) return;
		await dispatch(removeUser({ id: selected.id }));
		deleteModal.onClose();
	};

	const handlePassword = async () => {
		if (!selected) return;
		if (newPassword.length < 6) {
			toastService.warning('A senha deve ter ao menos 6 caracteres');
			return;
		}
		await dispatch(setUserPassword({ id: selected.id, newPassword }));
		setNewPassword('');
		passwordModal.onClose();
	};

	const handleCredits = async () => {
		if (!selected) return;
		const amount = Number(creditAmount);
		if (!creditAmount || Number.isNaN(amount) || amount === 0) {
			toastService.warning('Informe uma quantidade diferente de zero (use "-" para debitar)');
			return;
		}
		await dispatch(adjustUserCredits({ id: selected.id, amount, description: creditDesc }));
		creditsModal.onClose();
	};

	const handlePrice = async () => {
		if (!selected) return;
		const price = Number(priceValue);
		if (priceValue === '' || Number.isNaN(price) || price < 0) {
			toastService.warning('Informe um preço válido (0 ou maior)');
			return;
		}
		await dispatch(editUserPrice({ id: selected.id, pricePerQuery: price }));
		priceModal.onClose();
	};

	// Campo vazio vira null: limpa o override e devolve o cliente ao padrão global.
	const parseOverride = (value: string): number | null | undefined => {
		if (value.trim() === '') return null;
		const parsed = Number(value);
		return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
	};

	const handleLeilaoPrices = async () => {
		if (!selected) return;
		const today = parseOverride(leilaoToday);
		const old = parseOverride(leilaoOld);
		if (today === undefined || old === undefined) {
			toastService.warning('Informe preços válidos (0 ou maior), ou deixe vazio para usar o padrão');
			return;
		}
		await dispatch(editUserLeilao({ id: selected.id, leilaoPriceToday: today, leilaoPriceOld: old }));
		leilaoModal.onClose();
	};

	return (
		<Box pt='10px'>
			<Card>
				<Flex justify='space-between' align='center' mb='16px'>
					<Text fontWeight='800' fontSize='lg' color={textColor}>
						Usuários
					</Text>
					<Button variant='brand' leftIcon={<Icon as={MdPersonAdd} />} onClick={createModal.onOpen}>
						Novo usuário
					</Button>
				</Flex>

				{loading && list.length === 0 && <Spinner color='brand.500' />}

				<Box overflowX='auto'>
					<Table variant='simple' size='sm'>
						<Thead>
							<Tr>
								<Th>Nome</Th>
								<Th>E-mail</Th>
								<Th>Papel</Th>
								<Th>Indicador</Th>
								<Th isNumeric>Saldo</Th>
								<Th isNumeric>Qtd. consultas</Th>
								<Th isNumeric>Preço/consulta</Th>
								<Th>Leilão</Th>
								<Th>Ativo</Th>
								<Th>Ações</Th>
							</Tr>
						</Thead>
						<Tbody>
							{list.map(user => (
								<Tr key={user.id}>
									<Td fontWeight='600' color={textColor}>
										{user.name}
									</Td>
									<Td color={subColor}>{user.email}</Td>
									<Td>
										<Badge
											colorScheme={user.role === Role.ADMIN ? 'purple' : user.role === Role.SUPORTE ? 'blue' : 'gray'}
											borderRadius='8px'>
											{user.role}
										</Badge>
									</Td>
									<Td color={subColor}>{user.indicator?.name || '—'}</Td>
									<Td isNumeric color={textColor}>
										{formatBRL(user.balance)}
									</Td>
									{/* Saldo ÷ preço por consulta ('∞' quando a consulta é gratuita). */}
									<Td isNumeric color={textColor} fontWeight='600'>
										{formatQueries(user.balance, user.pricePerQuery)}
									</Td>
									<Td isNumeric color={textColor}>
										{formatPrice(user.pricePerQuery)}
									</Td>
									{/* Leilão é liberado cliente a cliente; os preços ficam no modal. */}
									<Td>
										<Switch
											variant='main'
											isChecked={user.leilaoEnabled}
											onChange={() =>
												dispatch(editUserLeilao({ id: user.id, leilaoEnabled: !user.leilaoEnabled }))
											}
											colorScheme='blue'
										/>
									</Td>
									<Td>
										<Switch
											variant='main'
											isChecked={user.active}
											isDisabled={!isAdmin}
											onChange={() => dispatch(toggleUserActive({ id: user.id, active: !user.active }))}
											colorScheme='blue'
										/>
									</Td>
									<Td>
										<Flex gap='6px'>
											{/* Dados cadastrais e senha são só do ADMIN; SUPORTE fica no preço. */}
											{isAdmin && (
												<Tooltip label='Editar dados' fontSize='xs'>
													<Button size='xs' variant='light' onClick={() => openEdit(user)}>
														<Icon as={MdEdit} />
													</Button>
												</Tooltip>
											)}
											<Tooltip label='Preços do leilão' fontSize='xs'>
												<Button size='xs' variant='light' onClick={() => openLeilao(user)}>
													<Icon as={MdGavel} />
												</Button>
											</Tooltip>
											<Tooltip label='Editar preço por consulta' fontSize='xs'>
												<Button size='xs' variant='light' onClick={() => openPrice(user)}>
													<Icon as={MdLocalOffer} />
												</Button>
											</Tooltip>
											{isAdmin && (
												<Tooltip label='Redefinir senha' fontSize='xs'>
													<Button size='xs' variant='light' onClick={() => openPassword(user)}>
														<Icon as={MdVpnKey} />
													</Button>
												</Tooltip>
											)}
											{isAdmin && (
												<Tooltip label='Ajustar créditos' fontSize='xs'>
													<Button size='xs' variant='light' color='green.500' onClick={() => openCredits(user)}>
														<Icon as={MdAttachMoney} />
													</Button>
												</Tooltip>
											)}
											{/* O servidor também barra a auto-exclusão; esconder evita o clique frustrado. */}
											{isAdmin && user.id !== userId && (
												<Tooltip label='Excluir usuário' fontSize='xs'>
													<Button size='xs' variant='light' color='red.500' onClick={() => openDelete(user)}>
														<Icon as={MdDelete} />
													</Button>
												</Tooltip>
											)}
										</Flex>
									</Td>
								</Tr>
							))}
						</Tbody>
					</Table>
				</Box>
			</Card>

			{/* Create user modal */}
			<Modal isOpen={createModal.isOpen} onClose={createModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Novo usuário</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Nome</FormLabel>
							<Input variant='main' value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
						</FormControl>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>E-mail</FormLabel>
							<Input variant='main' type='email' value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
						</FormControl>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Senha</FormLabel>
							<PasswordInput value={form.password} onChange={password => setForm({ ...form, password })} />
						</FormControl>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Papel</FormLabel>
							{/* Suporte só cria usuários comuns (USER); admin cria qualquer papel. */}
							<Select
								variant='main'
								value={form.role}
								isDisabled={!isAdmin}
								onChange={e => setForm({ ...form, role: e.target.value as Role })}>
								<option value={Role.USER}>USER</option>
								{isAdmin && <option value={Role.SUPORTE}>SUPORTE</option>}
								{isAdmin && <option value={Role.ADMIN}>ADMIN</option>}
							</Select>
						</FormControl>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Preço por consulta (R$)</FormLabel>
							<Input
								variant='main'
								inputMode='decimal'
								placeholder='Ex: 0.015'
								value={form.pricePerQuery}
								onChange={e => {
									const v = e.target.value;
									if (v === '' || /^\d*\.?\d*$/.test(v)) setForm({ ...form, pricePerQuery: v });
								}}
							/>
						</FormControl>
						<FormControl>
							<FormLabel fontSize='sm'>Cliente de (indicador)</FormLabel>
							<Select
								variant='main'
								value={form.indicatorId}
								onChange={e => setForm({ ...form, indicatorId: e.target.value })}>
								<option value=''>— Sem indicador —</option>
								{indicators.map(i => (
									<option key={i.id} value={i.id}>
										{i.name}
									</option>
								))}
							</Select>
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={createModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handleCreate}>
							Criar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Edit user modal */}
			<Modal isOpen={editModal.isOpen} onClose={editModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Editar usuário — {selected?.name}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Nome</FormLabel>
							<Input variant='main' value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
						</FormControl>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>E-mail</FormLabel>
							<Input
								variant='main'
								type='email'
								value={editForm.email}
								onChange={e => setEditForm({ ...editForm, email: e.target.value })}
							/>
						</FormControl>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Papel</FormLabel>
							<Select
								variant='main'
								value={editForm.role}
								onChange={e => setEditForm({ ...editForm, role: e.target.value as Role })}>
								<option value={Role.USER}>USER</option>
								<option value={Role.SUPORTE}>SUPORTE</option>
								<option value={Role.ADMIN}>ADMIN</option>
							</Select>
						</FormControl>
						<FormControl>
							<FormLabel fontSize='sm'>Cliente de (indicador)</FormLabel>
							<Select
								variant='main'
								value={editForm.indicatorId}
								onChange={e => setEditForm({ ...editForm, indicatorId: e.target.value })}>
								<option value=''>— Sem indicador —</option>
								{indicators.map(i => (
									<option key={i.id} value={i.id}>
										{i.name}
									</option>
								))}
							</Select>
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={editModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handleEdit}>
							Salvar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Delete confirmation modal */}
			<Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Excluir usuário</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text color={textColor}>
							Excluir <strong>{selected?.name}</strong> ({selected?.email})?
						</Text>
						<Text fontSize='sm' color={subColor} mt='10px'>
							O acesso é revogado na hora e o usuário some da lista. O histórico de consultas e o extrato de
							créditos são preservados, e o e-mail fica livre para um novo cadastro.
						</Text>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={deleteModal.onClose}>
							Cancelar
						</Button>
						<Button colorScheme='red' onClick={handleDelete}>
							Excluir
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Admin reset password modal */}
			<Modal isOpen={passwordModal.isOpen} onClose={passwordModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Redefinir senha — {selected?.name}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl>
							<FormLabel fontSize='sm'>Nova senha</FormLabel>
							<PasswordInput value={newPassword} onChange={setNewPassword} placeholder='Mínimo 6 caracteres' />
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={passwordModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handlePassword}>
							Salvar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Adjust credits modal */}
			<Modal isOpen={creditsModal.isOpen} onClose={creditsModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Ajustar créditos — {selected?.name}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Quantidade (use negativo para debitar)</FormLabel>
							<Input
								variant='main'
								inputMode='decimal'
								placeholder='Ex: 50 ou -20'
								value={creditAmount}
								onChange={e => {
									const v = e.target.value;
									// Aceita vazio, "-", dígitos e um ponto decimal.
									if (v === '' || /^-?\d*\.?\d*$/.test(v)) setCreditAmount(v);
								}}
							/>
						</FormControl>
						<FormControl>
							<FormLabel fontSize='sm'>Descrição</FormLabel>
							<Input variant='main' value={creditDesc} onChange={e => setCreditDesc(e.target.value)} />
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={creditsModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handleCredits}>
							Confirmar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Edit price modal */}
			<Modal isOpen={leilaoModal.isOpen} onClose={leilaoModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Preços do leilão — {selected?.name}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text fontSize='sm' color={subColor} mb='12px'>
							Deixe em branco para este cliente usar o preço padrão definido em Integrações.
						</Text>
						<FormControl mb='12px'>
							<FormLabel fontSize='sm'>Lead do dia (R$)</FormLabel>
							<Input
								variant='main'
								inputMode='decimal'
								placeholder='Padrão global'
								value={leilaoToday}
								onChange={e => {
									const v = e.target.value;
									if (v === '' || /^\d*\.?\d*$/.test(v)) setLeilaoToday(v);
								}}
							/>
						</FormControl>
						<FormControl>
							<FormLabel fontSize='sm'>Lead mais antigo (R$)</FormLabel>
							<Input
								variant='main'
								inputMode='decimal'
								placeholder='Padrão global'
								value={leilaoOld}
								onChange={e => {
									const v = e.target.value;
									if (v === '' || /^\d*\.?\d*$/.test(v)) setLeilaoOld(v);
								}}
							/>
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={leilaoModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handleLeilaoPrices}>
							Salvar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			<Modal isOpen={priceModal.isOpen} onClose={priceModal.onClose} isCentered>
				<ModalOverlay />
				<ModalContent borderRadius='20px'>
					<ModalHeader>Editar preço — {selected?.name}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormControl>
							<FormLabel fontSize='sm'>Preço por consulta (R$)</FormLabel>
							<Input
								variant='main'
								inputMode='decimal'
								placeholder='Ex: 1.50'
								value={priceValue}
								onChange={e => {
									const v = e.target.value;
									// Aceita vazio, dígitos e um ponto decimal (sem negativo).
									if (v === '' || /^\d*\.?\d*$/.test(v)) setPriceValue(v);
								}}
							/>
						</FormControl>
					</ModalBody>
					<ModalFooter gap='10px'>
						<Button variant='light' onClick={priceModal.onClose}>
							Cancelar
						</Button>
						<Button variant='brand' onClick={handlePrice}>
							Salvar
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
}
